import { useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { EditIcon } from 'foundations-assets/icons/components/EditIcon';
import { PlayerPlayIcon } from 'foundations-assets/icons/components/PlayerPlayIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Link } from 'foundations-components/transitional/components/Link';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import { CardMenuChangePlaylistThumbnailOption, MobileMenuChangePlaylistThumbnailOption } from 'academy-common-lib/features/savedItems/components/CardMenuChangePlaylistThumbnailOption';
import { usePlaylistButtonState } from 'academy-common-lib/features/savedItems/components/StartPlaylistButton';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import MobileNestedMenu from 'academy-common-lib/components/Modals/FullScreenModal/MobileNestedMenu';
import { useCanUserEditPlaylist } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import { UploadPlaylistThumbnailButton } from 'academy-common-lib/features/savedItems/components/UploadPlaylistThumbnailButton';
import { getPlaylistUrl } from '../../../utils/RoutingUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OverlayContainer = styled.div.withConfig({
  displayName: "PlaylistThumbnailOverlay__OverlayContainer"
})(["position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transition:0.3s ease background-color,0.3s ease opacity;padding:", ";& > *{opacity:", ";transition:0.3s ease opacity;}a{text-decoration:none;&:hover{text-decoration:underline;}}", ""], ({
  theme
}) => theme.space['500'], ({
  $alwaysVisible
}) => $alwaysVisible ? 1 : 0, ({
  theme,
  $alwaysVisible
}) => !$alwaysVisible && `
    &:hover,
    &:focus-within,
    &.menu-open {
      background-color: rgba(0, 0, 0, 0.65);

      & > * {
        opacity: 1;
      }

      a {
        color: ${theme.color['text-core-on-fill-default']} !important;
      }
    }
  `);
const ThumbnailChangeButton = ({
  hasThumbnail,
  onClick,
  use = 'blue'
}) => /*#__PURE__*/_jsxs(Link, {
  onClick: onClick,
  onKeyDown: e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onClick();
    }
  },
  use: use,
  children: [/*#__PURE__*/_jsx(IconHolder, {
    icon: hasThumbnail ? EditIcon : AddIcon
  }), hasThumbnail ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.thumbnailBlankChangeThumbnail"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.thumbnailBlankChooseThumbnail"
  })]
});
const StartPlaylistOverlay = ({
  playlistId
}) => {
  const {
    url,
    buttonText,
    handlePlaylistStart,
    isDisabled
  } = usePlaylistButtonState(playlistId, 'playlist');
  const handleClick = () => {
    handlePlaylistStart('playlist-thumbnail');
  };
  if (isDisabled) return /*#__PURE__*/_jsx("div", {
    "data-test-id": "no-overlay"
  });
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(OverlayContainer, {
      "data-test-id": "playlist-thumbnail-start-overlay",
      children: /*#__PURE__*/_jsx(RouterLink, {
        color: "text-core-on-fill-default",
        onClick: handleClick,
        to: url || getPlaylistUrl(playlistId),
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          blockSize: "100%",
          flexDirection: "column",
          justifyContent: "center",
          children: [/*#__PURE__*/_jsx(IconHolder, {
            color: "text-core-on-fill-default",
            icon: PlayerPlayIcon,
            size: "700"
          }), /*#__PURE__*/_jsx(Text, {
            color: "text-core-on-fill-default",
            typography: "heading-50",
            children: buttonText
          })]
        })
      })
    })
  );
};
const ChangeThumbnailOverlay = ({
  playlist
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasThumbnail = !!playlist.thumbnailRectangleUrl;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(OverlayContainer, {
      $alwaysVisible: !hasThumbnail,
      className: isMenuOpen ? 'menu-open' : '',
      children: /*#__PURE__*/_jsx(CardMenuChangePlaylistThumbnailOption, {
        onOpenChange: setIsMenuOpen,
        playlistId: playlist.id,
        render: handleClick => /*#__PURE__*/_jsx(ThumbnailChangeButton, {
          hasThumbnail: hasThumbnail,
          onClick: handleClick,
          use: hasThumbnail ? 'on-dark' : 'blue'
        })
      })
    })
  );
};
const MobileChangeThumbnailOverlay = ({
  playlist
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(OverlayContainer, {
      $alwaysVisible: true,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        blockSize: "100%",
        justifyContent: "center",
        padding: "500",
        children: [/*#__PURE__*/_jsx(ThumbnailChangeButton, {
          hasThumbnail: !!playlist.thumbnailRectangleUrl,
          onClick: () => setIsMenuOpen(true),
          use: "blue"
        }), isMenuOpen && /*#__PURE__*/_jsx(MobileNestedMenu, {
          indented: false,
          open: true,
          setIsOpen: openState => setIsMenuOpen(openState),
          children: /*#__PURE__*/_jsx(MobileMenuChangePlaylistThumbnailOption, {
            onItemClick: () => setIsMenuOpen(false),
            playlistId: playlist.id
          })
        })]
      })
    })
  );
};
const EmptyPlaylistOverlay = ({
  playlist
}) => {
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(OverlayContainer, {
      $alwaysVisible: true,
      children: /*#__PURE__*/_jsxs(UploadPlaylistThumbnailButton, {
        playlistId: playlist.id,
        responsive: false,
        use: "transparent",
        children: [/*#__PURE__*/_jsx(AddIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.playlists.uploadPlaylistThumbnail"
        })]
      })
    })
  );
};
const PlaylistThumbnailOverlay = ({
  playlist
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const canUserEditPlaylist = useCanUserEditPlaylist(playlist.id);
  const hasThumbnail = !!playlist.thumbnailRectangleUrl;
  const hasItems = !!playlist.itemsCount;
  const isEmptyPlaylist = !hasItems && !hasThumbnail;
  if (isMobile && hasThumbnail) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "no-overlay"
    });
  }
  if (!canUserEditPlaylist) {
    if (isMobile) {
      return /*#__PURE__*/_jsx("div", {
        "data-test-id": "no-overlay"
      });
    }
    return /*#__PURE__*/_jsx(StartPlaylistOverlay, {
      playlistId: playlist.id
    });
  }
  if (isEmptyPlaylist) {
    return /*#__PURE__*/_jsx(EmptyPlaylistOverlay, {
      playlist: playlist
    });
  }
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobileChangeThumbnailOverlay, {
      playlist: playlist
    });
  }
  return /*#__PURE__*/_jsx(ChangeThumbnailOverlay, {
    playlist: playlist
  });
};
export default PlaylistThumbnailOverlay;